import { isEmpty } from "lodash";
import { langToLocale } from "vue-app/utils/functions";
import { mapInitialChannelType, updateStateByChannelType } from "./helpers/messageChannelHelpers";
import { UNLAYER_MESSAGE } from "./helpers/editorDesignConst";
import { deserializeDesign } from "./helpers/messageEncoding";
import { getEngagementDesignState } from "./state";

export default {
  setDefaultState(state) {
    // merge to keep observers in place
    Object.assign(state, getEngagementDesignState());
  },
  initialState(state, engagementConfig) {
    state.orderIndex = engagementConfig.orderIndex;
    state.orderIndexes = engagementConfig.orderIndexes;
    state.engagementTypeId = engagementConfig.engagementTypeId;
    state.engagementLanguages = engagementConfig.engagementLanguages;
    state.isMultiLanguage = engagementConfig.isMultiLanguage;
    state.isEmptyCampaign = engagementConfig.isEmptyCampaign;
    state.redirects = engagementConfig.redirects;
    state.engagement = engagementConfig.engagement;
    state.engagementId = engagementConfig.engagementId;
    state.newsletterSubscriptionChannelType = engagementConfig.channelType;
    state.engagementMessageId = engagementConfig.engagementMessageId;
    state.couponPoolId = engagementConfig.couponPoolId;
    state.engagementMessageCount = engagementConfig.engagementMessageCount;
    state.additionalMessageCount = engagementConfig.additionalMessageCount;
    state.optInTypeId = engagementConfig.optInTypeId;
    state.recommendationEventTypeId = engagementConfig.recommendationEventTypeId;
    state.groups = engagementConfig.groups;
    state.utm = engagementConfig.utm;
    state.sender = engagementConfig.sender;
    state.isDev = engagementConfig.isDev;
    state.isDemo = engagementConfig.isDemo;
    mapInitialChannelType(state, engagementConfig.channelType);

    if (state.isMultiLanguage) {
      state.messageLanguage = engagementConfig.engagementMessage?.engagementMessageLanguage;
    }

    if (state.groups) {
      state.groupId = engagementConfig.engagementMessage?.engagementMessageGroupId;
    }

    if (engagementConfig.message) {
      state.message = {
        subject: engagementConfig.message.subject,
        preheader: engagementConfig.engagementMessage.preheader,
        html: engagementConfig.message.html,
        design: isEmpty(engagementConfig.message.design)
          ? { newCampaign: true }
          : deserializeDesign(engagementConfig.message.design),
      };
    }

    const storedMessage = window.localStorage.getItem(UNLAYER_MESSAGE);
    if (storedMessage) {
      state.messageClipboard = {
        hasData: true,
        ...JSON.parse(storedMessage),
      };
    }

    updateStateByChannelType(state, engagementConfig.channelType);
  },
  setMessageData(state, messageData) {
    state.engagementTypeId = messageData.engagementTypeId;
    state.orderIndex = messageData.orderIndex;
    state.groupId = messageData.groupId;
    state.editorType = messageData.editorType;
    state.message = {
      ...state.message,
      subject: messageData.subject,
      preheader: messageData.preheader,
    };
    state.messageLanguage = messageData.messageLanguage;
  },
  applyLoadedMessage(state, loadedMessage) {
    state.isMessageModified = true;
    state.engagementMessageId = loadedMessage.engagementMessageId;
    state.messageChannelType = loadedMessage.engagementMessageChannelType;
    state.messageLanguage = loadedMessage.engagementMessageLanguage;
    state.orderIndex = loadedMessage.engagementMessageOrderIndex;
    state.couponPoolId = loadedMessage.couponPoolId;
    state.groupId = loadedMessage.engagementMessageGroupId;
    state.message = {
      subject: loadedMessage.subject,
      preheader: loadedMessage.preheader,
      html: loadedMessage.html,
      design: loadedMessage.editorLayout ? deserializeDesign(loadedMessage.editorLayout) : { newCampaign: true },
    };
    state.utm.campaign = loadedMessage.utmCampaign;
    state.utm.medium = loadedMessage.utmMedium;
    state.utm.source = loadedMessage.utmSource;
    state.utm.areOverwritten = loadedMessage.areGaTagsOverwritten;

    updateStateByChannelType(state, loadedMessage.channelType);
  },
  messageProperties(state, selectorValues) {
    ["orderIndex", "groupId", "messageLanguage", "availableChannelTypes", "messageChannelType"].forEach((key) => {
      if (selectorValues.hasOwnProperty(key)) {
        state[key] = selectorValues[key];
      }
    });
  },
  message(state, { subject, design, html }) {
    state.isMessageModified = true;
    state.message = {
      subject,
      design,
      html,
    };
  },
  messageContent(state, message) {
    state.isMessageModified = true;
    state.message.design = message.design;
    state.message.html = message.html;
  },
  messageSubject(state, subject) {
    state.isMessageModified = true;
    state.message.subject = subject;
  },
  messagePreheader(state, preheader) {
    state.isMessageModified = true;
    state.message.preheader = preheader;
  },
  couponPoolId(state, couponPoolId) {
    state.couponPoolId = couponPoolId;
  },
  copyMessageToClipboard(state) {
    state.messageClipboard = {
      hasData: true,
      ...state.message,
    };
  },
  pasteMessageFromLocalStorage(state) {
    state.isMessageModified = true;
    const { html, subject, design, preheader } = state.messageClipboard;
    state.message = {
      html,
      subject,
      preheader,
      design,
    };
  },
  pasteMessageFromClipboard(state, message) {
    state.isMessageModified = true;
    state.message = {
      subject: message.subject || state.message.subject,
      preheader: message.preheader || state.message.preheader,
      design: message.design,
      html: message.html || "",
    };
  },
  editorReady(state, isEditorReady) {
    state.isEditorReady = isEditorReady;
  },
  messageLanguage(state, language) {
    state.messageLanguage = language;
  },
  utmCampaign(state, value) {
    state.utm.campaign = value;
  },
  utmSource(state, value) {
    state.utm.source = value;
  },
  utmMedium(state, value) {
    state.utm.medium = value;
  },
  utmTerm(state, value) {
    state.utm.term = value;
  },
  areUTMsOverwritten(state, value) {
    state.utm.areOverwritten = value;
  },
  isLoading(state, value) {
    state.isLoading = value;
  },
  isNewMessageLoading(state, value) {
    state.isNewMessageLoading = value;
  },
  messageHistory(state, history) {
    state.messageHistory = history;
  },
  templatesVisibility(state, isTemplatesSectionVisible) {
    state.isTemplatesSectionVisible = isTemplatesSectionVisible;
  },
  templatesLoading(state, templatesLoading) {
    state.templatesLoading = templatesLoading;
  },
  templates(state, templates) {
    state.templates = templates;
  },
  setEditor(state, editor) {
    state.editor = editor;
  },
};
